import { useState } from "react";
import { Badge, Box, Button, Group, NumberInput, Text } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import { updateFlowDetailSampleRate } from "../api";
import {
  formatLocalDay,
  hasEnded,
  lastDayForUntilInstant,
  localDateString,
  untilInstantForLastDay,
} from "./detailSampleRateDates";

/**
 * The flow's own sample rate for full request/response text, optionally until a date. It is set
 * through its own endpoint, so the flow settings form above cannot erase it.
 */
export default function FlowDetailSampleRate({ flow, setFlow }) {
  const current = flow.detailSampleRate;
  const [percent, setPercent] = useState(
    current ? Math.round(current.sampleRate * 100) : ""
  );
  const [lastDay, setLastDay] = useState(
    current?.until ? lastDayForUntilInstant(current.until) : null
  );
  const [saving, setSaving] = useState(false);

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const save = (dto, message) => {
    setSaving(true);
    updateFlowDetailSampleRate(flow.id, dto)
      .then((data) => {
        setFlow(data);
        const saved = data.detailSampleRate;
        setPercent(saved ? Math.round(saved.sampleRate * 100) : "");
        setLastDay(saved?.until ? lastDayForUntilInstant(saved.until) : null);
        notifications.show({ title: "Sample rate saved", message, color: "teal" });
      })
      // The API layer already shows the error.
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  const ended = hasEnded(current?.until);

  return (
    <Box mt="lg">
      <Group gap="xs" mb={4}>
        <Text size="sm" fw={500}>
          Sample rate for full text
        </Text>
        {current && !ended && (
          <Badge size="sm" variant="light" color="teal">
            {Math.round(current.sampleRate * 100)} %
            {current.until
              ? ` until ${formatLocalDay(lastDayForUntilInstant(current.until))}`
              : ""}
          </Badge>
        )}
        {current && ended && (
          <Badge size="sm" variant="light" color="gray">
            Ended
          </Badge>
        )}
      </Group>
      <Text size="xs" c="dimmed" mb="xs">
        Share of this flow&apos;s runs that keep their full prompt and answer
        text. A run keeps its text when it falls in the highest rate that
        applies: the tenant rate, the rate of its environment, or this rate. So
        this rate can only keep more runs; a rate below the tenant rate has no
        effect. After the end date, the flow follows the other rates again.
      </Text>
      <Group align="flex-end">
        <NumberInput
          label="Rate (%)"
          placeholder="Not set"
          min={0}
          max={100}
          allowDecimal={false}
          suffix="%"
          value={percent}
          onChange={setPercent}
          w={140}
        />
        <DatePickerInput
          label="Last day (optional)"
          placeholder="No end"
          clearable
          minDate={localDateString(tomorrow)}
          value={lastDay}
          onChange={setLastDay}
          w={200}
        />
        <Button
          variant="outline"
          loading={saving}
          disabled={percent === "" || percent == null}
          onClick={() =>
            save(
              {
                sampleRate: Number(percent) / 100,
                until: lastDay ? untilInstantForLastDay(lastDay) : null,
              },
              "The flow's sample rate applies to new runs."
            )
          }
        >
          Save rate
        </Button>
        {current && (
          <Button
            variant="subtle"
            color="gray"
            loading={saving}
            onClick={() =>
              save(
                { sampleRate: null, until: null },
                "The flow follows the tenant and environment rates again."
              )
            }
          >
            Remove
          </Button>
        )}
      </Group>
    </Box>
  );
}
